import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { WizyIconComponent } from './icon.component';

export type IconTheme = 'angular-material-native' | 'mdi' | 'fa' | 'wizy';
export type IconColor = 'GREEN' | 'RED' | 'BLUE' | 'ORANGE' | 'YELLOW' | 'GREY' | 'WHITE' | 'BLACK' | 'PRIMARY' | string;

/**
 * Composant wrapper pour afficher des icônes de différentes librairies
 *
 * Usage :
 *
 * - Wizy (SVG depuis assets/icons/) :
 *   `<app-wizy-icon-wrapper name="home"></app-wizy-icon-wrapper>`
 *   ou
 *   `<app-wizy-icon-wrapper theme="wizy" iconName="home"></app-wizy-icon-wrapper>`
 *
 * - Material Design Icons (mdi) :
 *   `<app-wizy-icon-wrapper theme="mdi" iconName="home"></app-wizy-icon-wrapper>`
 *
 * - Font Awesome (fa) :
 *   `<app-wizy-icon-wrapper theme="fa" iconName="home"></app-wizy-icon-wrapper>`
 *
 * - Angular Material :
 *   `<app-wizy-icon-wrapper theme="angular-material-native" iconName="home"></app-wizy-icon-wrapper>`
 */
@Component({
    selector: 'app-wizy-icon-wrapper',
    standalone: true,
    imports: [CommonModule, WizyIconComponent],
    template: `
        <!-- Wizy SVG icons -->
        <app-wizy-icon *ngIf="theme === 'wizy'" [name]="iconName" [size]="size" [color]="colorValue"> </app-wizy-icon>

        <!-- Material Design Icons (mdi) -->
        <span *ngIf="theme === 'mdi'" class="mdi mdi-{{ iconName }}" [style.color]="colorValue" [style.fontSize.px]="sizeInPx"> </span>

        <!-- Font Awesome -->
        <span *ngIf="theme === 'fa'" class="fa fa-{{ iconName }}" [style.color]="colorValue" [style.fontSize.px]="sizeInPx"> </span>

        <!-- Angular Material Native (using ligatures) -->
        <span
            *ngIf="theme === 'angular-material-native'"
            [class]="fontSet || 'material-icons'"
            [style.color]="colorValue"
            [style.fontSize.px]="sizeInPx"
        >
            {{ iconName }}
        </span>
    `,
    styles: [
        `
            :host {
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }

            .mdi,
            .fa,
            .material-icons {
                display: inline-block;
                line-height: 1;
            }

            .material-icons-two-tone {
                color: transparent !important;
                background-clip: text;
                -webkit-background-clip: text;
            }

            :host(.success) .material-icons-two-tone {
                background-color: #4caf50 !important;
            }

            :host(.error) .material-icons-two-tone {
                background-color: #f44336 !important;
            }

            :host(.justify) .material-icons-two-tone {
                background-color: #ff9800 !important;
            }

            app-wizy-icon {
                display: inline-flex;
            }
        `,
    ],
})
export class IconWrapperComponent implements OnChanges {
    /** Thème d'icône à utiliser */
    @Input() theme: IconTheme = 'angular-material-native';

    /** Nom de l'icône dans le thème */
    @Input() iconName: string = '';

    /** Alias pour iconName (pour compatibilité avec wizy-icon) */
    @Input() name: string = '';

    /** Couleur de l'icône (hérite du parent si non définie) */
    @Input() color?: IconColor;

    /** Taille de l'icône */
    @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number = 'md';

    /** FontSet pour les icônes Angular Material (ex: 'material-icons-two-tone') */
    @Input() fontSet?: string;

    colorValue: string | undefined = undefined;
    sizeInPx: number = 24;

    private colorMap: Record<string, string> = {
        GREEN: '#4caf50',
        RED: '#f44336',
        BLUE: '#2196f3',
        ORANGE: '#ff9800',
        YELLOW: '#ffeb3b',
        GREY: '#9e9e9e',
        WHITE: '#ffffff',
        BLACK: '#000000',
        PRIMARY: '#3f51b5',
    };

    private sizeMap: Record<string, number> = {
        xs: 12,
        sm: 16,
        md: 24,
        lg: 32,
        xl: 48,
    };

    ngOnChanges(changes: SimpleChanges): void {
        // Use name if iconName is not set
        if (this.name && !this.iconName) {
            this.iconName = this.name;
        }

        // Map color (only if explicitly set, otherwise inherit from parent)
        if (this.color) {
            this.colorValue = this.colorMap[this.color.toUpperCase()] || this.color;
        } else {
            this.colorValue = undefined;
        }

        // Map size
        if (typeof this.size === 'string') {
            this.sizeInPx = this.sizeMap[this.size] || 24;
        } else {
            this.sizeInPx = this.size;
        }
    }
}
